import {Canvas, FabricImage, FabricText} from 'fabric'
import './App.css';
import {useCallback, useEffect, useRef, useState} from "react";
import {computeClipPath, createTextBackgroundPath, createTextCirclePath, exportImage} from "./Utils";
import {FabricObject} from "fabric/src/shapes/Object/FabricObject";

const maxCanvasSize = 500;
const defaultImagePath = 'european-flag-circle-default.png';
const defaultClipRadius = 250;
const defaultAngleOffset = -30
const defaultText = 'I Buy European';
const defaultTextSize = 81;
const defaultCharSpacing = 30;
const defaultTextRadius = 250;
const defaultTextColor = '#FFFFFF';
const defaultBackgroundColor = '#032f9a';
const defaultBgOpacity = 1;
const defaultBgLength = 164;

function App() {

    /**
     * Canvas state and initialisation
     */
    const canvasRef = useRef(null);
    const [canvas, setCanvas] = useState<Canvas>();

    // init canvas on ref
    useEffect(() => {
        const canvasSize = window.innerWidth < maxCanvasSize ? window.innerWidth : maxCanvasSize;

        if (canvasRef !== null && canvasRef.current !== null) {
            const initCanvas = new Canvas(canvasRef.current, {
                width: canvasSize,
                height: canvasSize,
                preserveObjectStacking: true
            });

            setCanvas(initCanvas);

            return () => {
                initCanvas.dispose();
            };
        }
    }, []);

    /**
     * File loading and image setting
     */
    const [image, setImage] = useState<FabricObject>();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const setBackgroundImage = useCallback((imageUrl: string, currentImage?: FabricObject) => {
        if (canvas) {
            const imgPromise = FabricImage.fromURL(imageUrl);

            imgPromise.then((img) => {
                // fit image in canvas
                let defaultScale = 1;
                if (img.width > canvas.width || img.height > canvas.height) {
                    defaultScale = Math.min(canvas.width / img.width, canvas.height / img.height);
                }

                // create biggest circle contained in image
                const initClipRadius = Math.floor(defaultScale * Math.min(img.width, img.height) / 2);
                setClipRadius(initClipRadius);

                // centre image in canvas
                const left = Math.max(0, (canvas.width - img.width * defaultScale) / 2);
                const top = Math.max(0, (canvas.height - img.height * defaultScale) / 2);

                img.set({
                    originX: 'left',
                    originY: 'top',
                    left: left,
                    top: top,
                    scaleX: defaultScale,
                    scaleY: defaultScale,
                    clipPath: computeClipPath(initClipRadius, defaultScale),
                    hasControls: false,
                    hasBorders: false,
                    selectable: false,
                    evented: false, // prevents image from being manipulated
                });

                // Remove old image if exists
                if (currentImage) {
                    canvas.remove(currentImage);
                }

                canvas.insertAt(0, img);
                canvas.renderAll();

                // workaround to get the image as a fabric object
                canvas.setActiveObject(img);
                setImage(canvas.getActiveObject());
            });
        }
    }, [canvas]);

    useEffect(() => {
        // use undefined parameter to fire effect only once after canvas is created
        setBackgroundImage(defaultImagePath, undefined);
    }, [canvas, setBackgroundImage]);

    const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !canvas) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const imageUrl = e.target?.result as string;
            setBackgroundImage(imageUrl, image)
        };

        reader.readAsDataURL(file);
    }, [canvas, setBackgroundImage, image]);

    const clickFileInput = () => {
        fileInputRef.current?.click()
    }

    /**
     * Clip radius management
     */
    const [clipRadius, setClipRadius] = useState<number>(defaultClipRadius);

    const handleClipRadius = useCallback((newClipRadius: number) => {
        setClipRadius(newClipRadius);
        if (image && canvas) {
            image.set('clipPath', computeClipPath(newClipRadius, image.scaleX));
            canvas.renderAll();
        }
    }, [image, canvas]);

    /**
     * Text background overlay management
     */
    const [clockwise, setClockwise] = useState<boolean>(false);
    const [angleOffset, setAngleOffset] = useState<number>(defaultAngleOffset);
    const [showBackground, setShowBackground] = useState<boolean>(true);
    const [bgOpacity, setBgOpacity] = useState<number>(defaultBgOpacity);
    const [bgLength, setBgLength] = useState<number>(defaultBgLength); // in degrees
    const [bgColor, setBgColor] = useState<string>(defaultBackgroundColor);
    const [textSize, setTextSize] = useState<number>(defaultTextSize);
    const [textRadius, setTextRadius] = useState<number>(defaultTextRadius);
    const [textBackground, setTextBackground] = useState<FabricObject>();

    const recreateTextBackgroundPath = useCallback((
        textRadius: number,
        textSize: number,
        angleOffset: number,
        bgLength: number,
        clockwise: boolean,
        bgColor: string,
        bgOpacity: number,
        showBackground: boolean,
        currentTextBackground?: FabricObject
    ) => {
        if (canvas) {
            const startAngle = clockwise ? 180 - angleOffset - 5 : 180 - angleOffset + 5;
            const outerRadius = clockwise ? textRadius : textRadius + 20;
            const innerRadius = outerRadius - textSize - 10;
            const newBgPath = createTextBackgroundPath(canvas.width, outerRadius, innerRadius, startAngle, bgLength, clockwise);
            newBgPath.set({
                fill: bgColor,
                opacity: showBackground ? bgOpacity : 0,
                hasControls: false,
                hasBorders: false,
                selectable: false,
                evented: false,
                selection: false,
            })

            if (currentTextBackground) {
                canvas.remove(currentTextBackground);
            }

            canvas.insertAt(1, newBgPath);
            canvas.renderAll();

            // workaround to get the image as a fabric object
            canvas.setActiveObject(newBgPath);
            setTextBackground(canvas.getActiveObject());
        }
    }, [canvas]);

    const updateBgLengthFromTextWidth = useCallback((textWidth: number, textRadius: number): number => {
        const arcLength = textWidth / (2 * Math.PI * textRadius) * 360 + 10;
        const maxedLength = Math.min(arcLength, 359.9);
        setBgLength(maxedLength);
        return maxedLength;
    }, []);

    const handleBgToggle = useCallback(() => {
        setShowBackground(!showBackground); // toggled so !showBackground is the result
        if (canvas && textBackground) {
            textBackground.set('opacity', showBackground ? 0 : bgOpacity);
            canvas.renderAll();
        }
    }, [showBackground, canvas, textBackground, bgOpacity]);

    const handleBgOpacity = useCallback((newOpacity: number) => {
        setBgOpacity(newOpacity);
        if (canvas && textBackground) {
            textBackground.set('opacity', showBackground ? newOpacity : 0);
            canvas.renderAll();
        }
    }, [canvas, showBackground, textBackground]);

    const handleBgLength = useCallback((newLength: number) => {
        const maxedLength = Math.min(newLength, 359.9);
        setBgLength(maxedLength);
        if (canvas && textBackground) {
            recreateTextBackgroundPath(textRadius, textSize, angleOffset, maxedLength, clockwise, bgColor, bgOpacity, showBackground, textBackground);
        }
    }, [angleOffset, bgColor, bgOpacity, canvas, clockwise, recreateTextBackgroundPath, showBackground, textBackground, textRadius, textSize]);

    const handleBgColor = useCallback((newBgColor: string) => {
        setBgColor(newBgColor);
        if (canvas && textBackground) {
            textBackground.set('fill', newBgColor);
            canvas.renderAll();
        }
    }, [canvas, textBackground]);

    /**
     * Text overlay management
     */
    const [text, setText] = useState<string>(defaultText);
    const [charSpacing, setCharSpacing] = useState<number>(defaultCharSpacing);
    const [textColor, setTextColor] = useState<string>(defaultTextColor);
    const [fabricText, setFabricText] = useState<FabricText>();

    const handleText = useCallback((newText: string) => {
        setText(newText);
        if (fabricText && canvas) {
            setFabricText(fabricText.set('text', newText));

            const newBgLength = updateBgLengthFromTextWidth(fabricText.calcTextWidth(), textRadius);
            if (canvas && textBackground) {
                recreateTextBackgroundPath(textRadius, textSize, angleOffset, newBgLength, clockwise, bgColor, bgOpacity, showBackground, textBackground);
            }

            canvas.renderAll();
        }
    }, [angleOffset, bgColor, bgOpacity, canvas, clockwise, fabricText, recreateTextBackgroundPath, showBackground, textBackground, textRadius, textSize, updateBgLengthFromTextWidth]);

    const updateTextRadius = useCallback((newTextRadius: number, clockwise: boolean) => {
        setTextRadius(newTextRadius);
        if (fabricText && canvas) {
            const newPath = createTextCirclePath(canvas.width, newTextRadius, clockwise);
            fabricText.set('path', newPath);
            fabricText.set('top', newPath.top);
            fabricText.set('left', newPath.left);

            // reset the angle at 0, or it makes the text rotate around the top-left corner
            fabricText.set('angle', 0);
            // then re-apply the rotation
            fabricText.rotate(-angleOffset);
        }
    }, [fabricText, canvas, angleOffset]);

    const handleTextRadius = useCallback((newTextRadius: number) => {
        updateTextRadius(newTextRadius, clockwise);

        if (fabricText && canvas) {
            const newBgLength = updateBgLengthFromTextWidth(fabricText.calcTextWidth(), newTextRadius);
            if (textBackground) {
                recreateTextBackgroundPath(newTextRadius, textSize, angleOffset, newBgLength, clockwise, bgColor, bgOpacity, showBackground, textBackground);
            }

            canvas.renderAll();
        }
    }, [angleOffset, bgColor, bgOpacity, canvas, clockwise, fabricText, recreateTextBackgroundPath, showBackground, textBackground, textSize, updateBgLengthFromTextWidth, updateTextRadius]);

    const handleClockwiseToggle = useCallback(() => {
        const newClockwise = !clockwise;
        setClockwise(newClockwise);
        if (fabricText && canvas) {
            let newTextRadius;
            let newCharSpacing;
            if (clockwise) { // toggled so anti-clockwise in the result
                newCharSpacing = Math.max(charSpacing - 80, 0);
                newTextRadius = Math.max(textRadius - 20, 0);
                fabricText.set('pathAlign', 'baseline');
                fabricText.set('charSpacing', newCharSpacing);
            } else {
                newCharSpacing = Math.max(charSpacing + 80, 0);
                newTextRadius = Math.max(textRadius + 20, 0);
                fabricText.set('pathAlign', 'ascender');
                fabricText.set('charSpacing', newCharSpacing);
            }
            setCharSpacing(newCharSpacing);
            updateTextRadius(newTextRadius, newClockwise);

            const newBgLength = updateBgLengthFromTextWidth(fabricText.calcTextWidth(), newTextRadius);
            if (textBackground) {
                recreateTextBackgroundPath(newTextRadius, textSize, angleOffset, newBgLength, newClockwise, bgColor, bgOpacity, showBackground, textBackground);
            }

            canvas.renderAll();
        }
    }, [angleOffset, bgColor, bgOpacity, canvas, charSpacing, clockwise, fabricText, recreateTextBackgroundPath, showBackground, textBackground, textRadius, textSize, updateBgLengthFromTextWidth, updateTextRadius]);

    const handleTextSize = useCallback((newTextSize: number) => {
        setTextSize(newTextSize || 10);
        if (fabricText && canvas) {
            fabricText.set('fontSize', newTextSize || 10);

            const newBgLength = updateBgLengthFromTextWidth(fabricText.calcTextWidth(), textRadius);
            if (canvas && textBackground) {
                recreateTextBackgroundPath(textRadius, newTextSize, angleOffset, newBgLength, clockwise, bgColor, bgOpacity, showBackground, textBackground);
            }

            canvas.renderAll();
        }
    }, [fabricText, canvas, updateBgLengthFromTextWidth, textRadius, textBackground, recreateTextBackgroundPath, angleOffset, clockwise, bgColor, bgOpacity, showBackground]);

    const handleCharSpacing = useCallback((newCharacterSpacing: number) => {
        setCharSpacing(newCharacterSpacing || 0);
        if (fabricText && canvas) {
            fabricText.set('charSpacing', newCharacterSpacing || 0);

            const newBgLength = updateBgLengthFromTextWidth(fabricText.calcTextWidth(), textRadius);
            if (canvas && textBackground) {
                recreateTextBackgroundPath(textRadius, textSize, angleOffset, newBgLength, clockwise, bgColor, bgOpacity, showBackground, textBackground);
            }

            canvas.renderAll();
        }
    }, [fabricText, canvas, updateBgLengthFromTextWidth, textRadius, textBackground, recreateTextBackgroundPath, textSize, angleOffset, clockwise, bgColor, bgOpacity, showBackground]);

    const handleTextColor = useCallback((newTextColor: string) => {
        setTextColor(newTextColor);
        if (fabricText && canvas) {
            fabricText.set('fill', newTextColor);
            canvas.renderAll();
        }
    }, [canvas, fabricText]);

    // moves both the text and its text background
    const handleAngleOffset = useCallback((newAngleOffset: number) => {
        setAngleOffset(newAngleOffset);
        if (fabricText && canvas) {
            // move the text
            fabricText.rotate(-newAngleOffset);

            // move the background
            recreateTextBackgroundPath(textRadius, textSize, newAngleOffset, bgLength, clockwise, bgColor, bgOpacity, showBackground, textBackground);

            canvas.renderAll();
        }
    }, [bgColor, bgLength, bgOpacity, canvas, clockwise, fabricText, recreateTextBackgroundPath, showBackground, textBackground, textRadius, textSize]);

    /**
     * Initialize the text and text background elements.
     */
    // N.B.: even if adding the other parameter as dependencies to fire the background update, it cannot ultimately
    // depend also on the textBackground fabric object, or it would be fired in a loop
    useEffect(() => {

        const scale = Math.min(window.innerWidth, maxCanvasSize) / maxCanvasSize;
        const initTextRadius = defaultTextRadius * scale - 20; // -20 to counter +20 of the background outer radius
        const initTextSize = defaultTextSize * scale;
        setTextRadius(initTextRadius);
        setTextSize(initTextSize);

        if (canvas) {
            const defaultPath = createTextCirclePath(canvas.width, initTextRadius, false);
            const initFabricText = new FabricText(defaultText, {
                fontSize: initTextSize,
                charSpacing: defaultCharSpacing,
                fontWeight: '600',
                fontFamily: 'Arial', // serif, sans-serif, monospace, cursive, Verdana, Arial, Tahoma
                path: defaultPath,
                pathAlign: 'baseline',
                top: defaultPath.top,
                left: defaultPath.left,
                angle: 0,
                fill: defaultTextColor,
                hasControls: false,
                hasBorders: false,
                selectable: false,
                evented: false,
                selection: false,
            })
            initFabricText.rotate(-defaultAngleOffset);
            setFabricText(initFabricText);

            const newBgLength = updateBgLengthFromTextWidth(initFabricText.calcTextWidth(), initTextRadius);
            // use undefined parameter to avoid dependency and fire effect only once after canvas is created
            recreateTextBackgroundPath(initTextRadius, initTextSize, defaultAngleOffset, newBgLength, false, defaultBackgroundColor, defaultBgOpacity, true, undefined);

            canvas.add(initFabricText);
            canvas.renderAll();
        }
    }, [canvas, recreateTextBackgroundPath, updateBgLengthFromTextWidth]);


    return (
        <div className="page">
            <div className="panesContainer">
                <div className="canvasPane">
                    <canvas ref={canvasRef} id="canvas" style={{border: "1px solid black"}}></canvas>
                    <input
                        ref={fileInputRef}
                        type="file"
                        id="fileInput"
                        style={{display: "none"}}
                        accept="image/png, image/jpeg"
                        onChange={handleFileSelect}
                    />
                    <div className="imageButtons">
                        <button onClick={clickFileInput}>Load profile picture</button>
                        <button onClick={() => exportImage(canvas)}>Download as an image</button>
                    </div>
                </div>

                <div className="settingsPane">
                    <h2>Canvas settings</h2>
                    <div className="settingsContainer">

                        <details open>
                            <summary>Text appearance</summary>

                            <div className="settingsGroup">
                                <label htmlFor="text">Text</label>
                                <input id="text" type="text" value={text} onChange={(e) => handleText(e.target.value)}/>

                                <label htmlFor="textSize">Text size</label>
                                <div>
                                    <div className="numberInput">
                                        <button onClick={() => handleTextSize(textSize - 1)}>-</button>
                                        <input id="textSize" type="number" inputMode="numeric" min="1" max="200"
                                               value={textSize}
                                               onChange={(e) => handleTextSize(e.target.valueAsNumber)}/>
                                        <button onClick={() => handleTextSize(textSize + 1)}>+</button>
                                    </div>
                                </div>

                                <label htmlFor="charSpacing">Character spacing</label>
                                <div>
                                    <div className="numberInput">
                                        <button onClick={() => handleCharSpacing(charSpacing - 1)}>-</button>
                                        <input id="charSpacing" type="number" inputMode="numeric" min="0" max="999"
                                               value={charSpacing}
                                               onChange={(e) => handleCharSpacing(e.target.valueAsNumber)}/>
                                        <button onClick={() => handleCharSpacing(charSpacing + 1)}>+</button>
                                    </div>
                                </div>

                                <label htmlFor="textColor">Text colour</label>
                                <div className="settingBox">
                                    <input id="textColor" type="color" value={textColor} list="presetColors"
                                           onChange={(e) => handleTextColor(e.target.value)}/>
                                    <datalist id="presetColors">
                                        <option>#ffffff</option>
                                        <option>#032f9a</option>
                                        <option>#FFCC00</option>
                                    </datalist>
                                    <input type="text" minLength={7} maxLength={7} size={7} placeholder="#FFCC00"
                                           pattern="/#[0-9]{6}/"
                                           value={textColor}
                                           onChange={(e) => handleTextColor(e.target.value)}/>
                                    <button onClick={() => handleTextColor(defaultTextColor)}>reset</button>
                                </div>
                            </div>
                        </details>

                        <div className="settingsDivider"></div>

                        <details>
                            <summary>Text position</summary>

                            <div className="settingsGroup">
                                <label htmlFor="clockwise">Clockwise</label>
                                <div className="settingBox">
                                    <input id="clockwise" type="checkbox" checked={clockwise}
                                           onChange={handleClockwiseToggle}/>
                                </div>

                                <label htmlFor="textRadius">Text radius</label>
                                <div className="settingBox">
                                    <input type="range" min="0" max={maxCanvasSize} value={textRadius}
                                           onChange={(e) => handleTextRadius(e.target.valueAsNumber)}/>

                                    <div className="numberInput">
                                        <button onClick={() => handleTextRadius(textRadius - 1)}>-
                                        </button>
                                        <input id="textRadius" type="number" inputMode="numeric" min="0"
                                               max={maxCanvasSize}
                                               size={4}
                                               value={textRadius}
                                               onChange={(e) => handleTextRadius(e.target.valueAsNumber)}/>
                                        <button onClick={() => handleTextRadius(textRadius + 1)}>+
                                        </button>
                                    </div>
                                </div>

                                <label htmlFor="textAngleOffset">Angle offset</label>
                                <div className="settingBox">
                                    <input type="range" min="-180" max="180" size={4}
                                           value={angleOffset}
                                           onChange={(e) => handleAngleOffset(e.target.valueAsNumber)}/>

                                    <div className="numberInput">
                                        <button onClick={() => handleAngleOffset(angleOffset - 1)}>-</button>
                                        <input id="textAngleOffset" type="number" inputMode="numeric" min="-180"
                                               max="180"
                                               value={angleOffset}
                                               onChange={(e) => handleAngleOffset(e.target.valueAsNumber)}/>
                                        <button onClick={() => handleAngleOffset(angleOffset + 1)}>+</button>

                                    </div>
                                </div>
                            </div>
                        </details>

                        <div className="settingsDivider"></div>

                        <details>
                            <summary>Image clipping</summary>

                            <div className="settingsGroup">
                                <label htmlFor="clipRadius">Clip radius</label>
                                <div className="settingBox">
                                    <input type="range" min="0" max={maxCanvasSize} value={clipRadius}
                                           onChange={(e) => handleClipRadius(e.target.valueAsNumber)}/>

                                    <div className="numberInput">
                                        <button onClick={() => handleClipRadius(clipRadius - 1)}>-</button>
                                        <input id="clipRadius" type="number" inputMode="numeric" min="0"
                                               max={maxCanvasSize}
                                               size={4}
                                               value={clipRadius}
                                               onChange={(e) => handleClipRadius(e.target.valueAsNumber)}/>
                                        <button onClick={() => handleClipRadius(clipRadius + 1)}>+</button>
                                    </div>
                                </div>
                            </div>
                        </details>

                        <div className="settingsDivider"></div>

                        <details>
                            <summary>Text background</summary>

                            <div className="settingsGroup">
                                <label htmlFor="showBackground">Text background</label>
                                <div className="settingBox">
                                    <input id="showBackground" type="checkbox" checked={showBackground}
                                           onChange={handleBgToggle}/>
                                </div>

                                <label htmlFor="bgOpacity">Background opacity</label>
                                <div className="settingBox">
                                    <input type="range" min="0" max="1" step="0.01" value={bgOpacity}
                                           onChange={(e) => handleBgOpacity(e.target.valueAsNumber)}/>

                                    <div className="numberInput">
                                        <button onClick={() => handleBgOpacity(bgOpacity - 0.01)}>-</button>
                                        <input id="bgOpacity" type="number" inputMode="decimal" min="0" max="1"
                                               step="0.01"
                                               value={bgOpacity.toFixed(2)}
                                               onChange={(e) => handleBgOpacity(e.target.valueAsNumber)}/>
                                        <button onClick={() => handleBgOpacity(bgOpacity + 0.01)}>+</button>
                                    </div>
                                </div>

                                <label htmlFor="bgLength">Background length</label>
                                <div className="settingBox">
                                    <input type="range" min="0" max="360" size={4}
                                           value={bgLength}
                                           onChange={(e) => handleBgLength(e.target.valueAsNumber)}/>

                                    <div className="numberInput">
                                        <button onClick={() => handleBgLength(bgLength - 1)}>-</button>
                                        <input id="bgLength" type="number" inputMode="numeric" min="0" max="360"
                                               value={bgLength}
                                               onChange={(e) => handleBgLength(e.target.valueAsNumber)}/>
                                        <button onClick={() => handleBgLength(bgLength + 1)}>+</button>
                                    </div>
                                </div>

                                <label htmlFor="bgColor">Background colour</label>
                                <div className="settingBox">
                                    <input id="bgColor" type="color" value={bgColor} list="presetColors"
                                           onChange={(e) => handleBgColor(e.target.value)}/>
                                    <input type="text" minLength={7} maxLength={7} size={7} placeholder="#FFCC00"
                                           pattern="/#[0-9]{6}/"
                                           value={bgColor}
                                           onChange={(e) => handleBgColor(e.target.value)}/>
                                    <button onClick={() => handleBgColor(defaultBackgroundColor)}>reset</button>
                                </div>
                            </div>
                        </details>

                    </div>
                </div>
            </div>
            <footer>
                Made and hosted in the European Union.
                <button popoverTarget="infopopover">More info</button>
                <div id="infopopover" popover="auto" className="popover">

                    <h3>Goal</h3>
                    <p>The ibuyfrom.eu website aims at helping to promote the "Buy European" movement.</p>
                    <p>
                        It is a <b>political</b> movement, because we, europeans, have purchasing power in what is the
                        greatest economic area in the world and we can signify our sentiment regarding the behaviour of
                        other countries political leaders by voting with our wallets and data. <s>Although this idea is
                        very much pro-european, it has no intention to be anti-someone else by openly calling to
                        boycotts.</s> Whether you think of it as pro-european or against the pathetic orange man and its
                        country's economy is up to you and may vary with time.
                    </p>
                    <p>
                        It is an <b>ecological</b> movement, because buying european means buying physical products
                        that are made closer to their place of purchase with less transportation involved, so with a
                        lesser carbon footprint (all other things considered equals). As manufacturing cost can be
                        higher in the European Union or Europe at large, it can also mean buying higher quality products
                        that last longer.
                    </p>
                    <p>
                        It is an <b>economy oriented</b> movement, because buying european means supporting the european
                        companies and economy in general. This will improve the trade imbalances, support european
                        employment and generate more tax revenue to the european states to support our social models.
                    </p>

                    <h3>Credits</h3>
                    <p>
                        The image manipulation logic uses the open source <a href="https://fabricjs.com/"
                                                                             target="none">fabric.js</a> library.
                    </p>

                    <h3>Pending features</h3>
                    <p>
                        Hereafter are some features I thought about but decided to postpone after the publication of
                        this first version of the website that I deemed good enough:
                    </p>
                    <ul>
                        <li>give a choice of fonts and boldness setting,</li>
                        <li>let the user re-center the loaded image,</li>
                        <li>give a choice of text path (circle or rounded square) to match profile pictures on some
                            websites,
                        </li>
                        <li>let the user add a coloured circle around the whole picture,</li>
                        <li>add an option to export the overlay only (without the image).</li>
                    </ul>
                    <p>I may work on these improvements in the future.</p>

                    <h3>Privacy policy</h3>
                    <p>
                        This website is obviously made with no cookie, nor tracker, nor browser fingerprinting. I will
                        edit this section if I start collecting anonymous usage statistics or traffic information for
                        pure curiosity and monitoring (never for data collection and resell).
                    </p>

                    <h3>Contact</h3>
                    <p>
                        You can contact me on my personal website <a
                        href="https://oliviercarron.com/en/mycontact" target="none">oliviercarron.com</a>.
                    </p>
                    <button popoverTarget="infopopover" popoverTargetAction="hide">Dismiss</button>
                </div>
            </footer>
        </div>
    );
}

export default App;
